// Sub-vistas, filtros, búsqueda y orden de la lista de códigos y cupones; todo corre en el cliente
// salvo el origen y el estado, que van al back.
import type { LifecycleStatusFilterOption } from '@/shared/patterns/Lifecycle';
import { matchesNameSearch } from '@/shared/patterns/Lifecycle';
import type {
    CouponStatus,
    DiscountCode,
    DiscountCodeOrigin,
    DiscountCodeStatus,
} from '../api/promotionsContract';
import type { DiscountCodeFilters } from '../api/useDiscountCodes';
import {
    ALL_CODE_STATUSES,
    CODE_STATUS_RANK,
    CODE_VIEW_STATUSES,
    DEFAULT_CODE_SORT,
} from '../constants/discountCodes';
import { CODE_STATUS_LABEL } from './discountCodesRow';

export type CodeStatus = DiscountCodeStatus | CouponStatus;

/** La sub-vista elegida es el `origin` que se le pide al back. */
export type CodeView = DiscountCodeOrigin;

/** Todo lo que no está vigente va atenuado en la lista. */
export function isDimmedCodeStatus(status: CodeStatus): boolean {
    return status !== 'ACTIVE';
}

export function codeStatusFilterOptions(view: CodeView): LifecycleStatusFilterOption[] {
    return CODE_VIEW_STATUSES[view].map((status) => ({
        value: status,
        label: CODE_STATUS_LABEL[status],
    }));
}

/** Filtros que van al back, sin llaves vacías: la query key solo ve lo puesto. */
export function toCodeFilters(view: CodeView, status: string): DiscountCodeFilters {
    return {
        origin: view,
        ...(status === ALL_CODE_STATUSES ? {} : { status: status as CodeStatus }),
    };
}

export function matchesCodeSearch(code: DiscountCode, query: string): boolean {
    return matchesNameSearch(code.codeText, query);
}

export type CodeSortKey = 'code' | 'status' | 'uses' | 'validity';

export interface CodeListSort {
    key: CodeSortKey;
    direction: 'asc' | 'desc';
}

/** Fin de vigencia comparable; sin fin (permanente) va al último. */
const validityEnd = (code: DiscountCode) =>
    code.validUntil === null ? Number.POSITIVE_INFINITY : Date.parse(code.validUntil);

const byCodeText = (a: DiscountCode, b: DiscountCode) =>
    a.codeText.localeCompare(b.codeText, 'es-MX');

function compareBy(key: CodeSortKey, a: DiscountCode, b: DiscountCode): number {
    switch (key) {
        case 'code':
            return byCodeText(a, b);
        case 'status':
            return CODE_STATUS_RANK[a.status] - CODE_STATUS_RANK[b.status];
        case 'uses':
            return a.uses - b.uses;
        case 'validity':
            return validityEnd(a) - validityEnd(b);
    }
}

export function toggleCodeSort(sort: CodeListSort, key: CodeSortKey): CodeListSort {
    return sort.key === key
        ? { key, direction: sort.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' };
}

/** Ordena por la columna elegida y, a empate, alfabético por el código; no toca la lista original. */
export function sortCodes<T extends DiscountCode>(
    items: readonly T[],
    sort: CodeListSort = DEFAULT_CODE_SORT,
): T[] {
    const sign = sort.direction === 'asc' ? 1 : -1;
    return [...items].sort((a, b) => sign * compareBy(sort.key, a, b) || byCodeText(a, b));
}
